import { type RefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/** Every strand is Studio green; the trail alpha below keeps it from taking over the page. */
const STRAND_COLOR = '#00f500';
/** Strands move a fixed distance per frame, so the field runs at 60 fps whatever the display's rate. */
const FRAME_MS = 1000 / 61;
/** Steps taken before the first frame, so it (and the only one, with reduced motion) is already full. */
const SETTLE_STEPS = 320;
/** Each trail is drawn in this many pieces, brighter toward its head. */
const TRAIL_PIECES = 5;
const TRAIL_ALPHA = 0.4;

interface Strand {
  /** The trail, oldest point first, as x, y pairs. */
  points: number[];
  maxPoints: number;
  age: number;
  life: number;
  speed: number;
}

/** Direction of the flow at a point: a few layered waves, smooth enough to read as silk. */
function flowAngle(x: number, y: number, t: number): number {
  return (
    Math.sin(x * 0.0016 + t * 0.6) * 1.3 +
    Math.cos(y * 0.0019 - t * 0.4) * 1.1 +
    Math.sin((x - y) * 0.0008 + t * 0.25) * 0.8
  );
}

/**
 * Coloured strands drifting through a flow field. Every frame is drawn from
 * scratch: each strand keeps its own trail and draws it brighter toward the
 * head, so nothing is left behind as it moves on. A strand that runs out of
 * life stops growing and its tail catches up before it starts again
 * elsewhere. With reduced motion it draws the settled field once.
 */
function useFlowField(canvasRef: RefObject<HTMLCanvasElement | null>) {
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return undefined;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let width = 0;
    let height = 0;
    let strands: Strand[] = [];
    let frame = 0;
    let t = Math.random() * 100;

    const spawn = (): Strand => ({
      points: [Math.random() * width, Math.random() * height],
      maxPoints: 140 + Math.floor(Math.random() * 160),
      age: 0,
      life: 260 + Math.random() * 420,
      speed: 1 + Math.random() * 1.1,
    });

    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      strands = Array.from({ length: Math.round((width * height) / 3600) }, spawn);
    };

    const move = () => {
      for (const strand of strands) {
        const { points } = strand;
        const x = points[points.length - 2];
        const y = points[points.length - 1];
        const inside = x > -10 && x < width + 10 && y > -10 && y < height + 10;
        strand.age += 1;
        if (strand.age < strand.life && inside) {
          const angle = flowAngle(x, y, t);
          points.push(x + Math.cos(angle) * strand.speed, y + Math.sin(angle) * strand.speed);
          if (points.length > strand.maxPoints * 2) points.splice(0, 2);
        } else if (points.length > 2) {
          points.splice(0, 4);
        } else {
          Object.assign(strand, spawn());
        }
      }
      t += 0.0025;
    };

    const draw = () => {
      context.clearRect(0, 0, width, height);
      context.globalCompositeOperation = 'lighter';
      context.lineWidth = 1.3;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      for (const { points, age } of strands) {
        const count = points.length / 2;
        if (count < 2) continue;
        const fadeIn = Math.min(1, age / 40);
        context.strokeStyle = STRAND_COLOR;
        for (let piece = 0; piece < TRAIL_PIECES; piece += 1) {
          const from = Math.floor(((count - 1) * piece) / TRAIL_PIECES);
          const to = Math.floor(((count - 1) * (piece + 1)) / TRAIL_PIECES);
          if (to <= from) continue;
          context.globalAlpha = TRAIL_ALPHA * fadeIn * ((piece + 1) / TRAIL_PIECES) ** 1.6;
          context.beginPath();
          context.moveTo(points[from * 2], points[from * 2 + 1]);
          for (let i = from + 1; i <= to; i += 1) context.lineTo(points[i * 2], points[i * 2 + 1]);
          context.stroke();
        }
      }
      context.globalAlpha = 1;
      context.globalCompositeOperation = 'source-over';
    };

    let last = 0;
    const loop = (now: number) => {
      if (now - last >= FRAME_MS) {
        last = now;
        move();
        draw();
      }
      frame = window.requestAnimationFrame(loop);
    };

    // Fires once on observe too: lays down a settled field at the canvas's size, then again on each resize.
    const observer = new ResizeObserver(() => {
      resize();
      for (let i = 0; i < SETTLE_STEPS; i += 1) move();
      draw();
    });
    observer.observe(canvas);
    if (!still) frame = window.requestAnimationFrame(loop);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [canvasRef]);
}

/** The sign-in page's backdrop: Studio as a place where things get made, flowing on behind the form. */
export function SignInArtwork() {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useFlowField(canvasRef);

  return (
    <div className="sign-in-art" aria-hidden="true">
      <canvas ref={canvasRef} className="sign-in-art__canvas" />
      <div className="sign-in-art__panel">
        <div className="sign-in-art__glow sign-in-art__glow--champagne" />
        <div className="sign-in-art__glow sign-in-art__glow--blue" />
        <div className="sign-in-art__glow sign-in-art__glow--violet" />
        <div className="sign-in-art__grid" />

        <div className="sign-in-art__stage">
          <div className="sign-in-art__piece sign-in-art__piece--prompt">
            <span className="sign-in-art__caret" />
            <p>{t('auth.art.prompt')}</p>
          </div>

          <div className="sign-in-art__piece sign-in-art__piece--app">
            <div className="sign-in-art__app-bar">
              <span />
              <span />
              <span />
            </div>
            <div className="sign-in-art__app-body">
              <div className="sign-in-art__map">
                <svg viewBox="0 0 200 120" preserveAspectRatio="none">
                  <path d="M0 86 C40 70 60 96 96 74 S150 40 200 52" />
                  <path d="M0 30 C50 44 80 18 120 32 S170 70 200 64" />
                  <path className="sign-in-art__route" d="M22 98 C60 80 82 60 110 58 S150 38 176 26" />
                </svg>
                <span className="sign-in-art__pin" />
              </div>
              <div className="sign-in-art__bars">
                {[42, 68, 54, 88, 61, 76, 94].map((height, index) => (
                  <span key={index} style={{ height: `${height}%` }} />
                ))}
              </div>
            </div>
          </div>

          <div className="sign-in-art__piece sign-in-art__piece--chip">
            <span className="sign-in-art__dot" />
            {t('auth.art.chip')}
          </div>
        </div>

        <div className="sign-in-art__caption">
          <p className="sign-in-art__eyebrow">RapidSOS Studio</p>
          <h2>{t('auth.art.headline')}</h2>
          <p>{t('auth.art.body')}</p>
        </div>
      </div>
    </div>
  );
}
